/**
 * 📜 QUESTS - 120 QUEST PROGRESSION SYSTEM
 * 
 * Designed for smooth progression with frequent rewards.
 * Rewards scaled to ~25% of current tier costs (helpful but not carrying).
 * 
 * STRUCTURE: Each chapter has 12 quests (10 original + 2 exploration)
 * - Quests 1-5: Core progression
 * - Quest 6: Exploration quest A
 * - Quests 7-11: Core progression  
 * - Quest 12: Exploration quest B
 * 
 * REWARD SCALING (2.5x per chapter, matching building/tech scaling):
 * | Chapter | Stage    | Reward Range |
 * |---------|----------|--------------|
 * |    1    | Tier 1-2 | 15-60        |
 * |    2    | Tier 2-3 | 40-150       |
 * |    3    | Tier 3-4 | 100-400      |
 * |    4    | Tier 4   | 250-1K       |
 * |    5    | Tier 5   | 600-2.5K     |
 * |    6    | Tier 5-6 | 1.5K-6K      |
 * |    7    | Tier 6-7 | 4K-15K       |
 * |    8    | Tier 7-8 | 10K-40K      |
 * |    9    | Tier 8-9 | 25K-100K     |
 * |   10    | Tier 9-11| 60K-250K     |
 */

// Helper to generate quest ID
const q = (num) => `quest_${String(num).padStart(3, '0')}`;

export const mainQuests = {
  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 1: AWAKENING (1-12) - Rewards: 15-60
  // ═══════════════════════════════════════════════════════════════════════════
  
  [q(1)]: { id: q(1), name: 'Into the Unknown', description: 'Explore 5 tiles.', chapter: 1,
    objectives: [{ type: 'tiles_explored', count: 5 }],
    rewards: { wood: 25, food: 20, gold: 10 } },
  
  [q(2)]: { id: q(2), name: 'First Light', description: 'Gather some wood to begin your new life.', chapter: 1,
    objectives: [{ type: 'resource', target: 'wood', count: 15 }],
    rewards: { wood: 15, food: 10 } },
  
  [q(3)]: { id: q(3), name: 'Survival', description: 'Gather wood and food to sustain yourself.', chapter: 1,
    objectives: [{ type: 'resource', target: 'wood', count: 25 }, { type: 'resource', target: 'food', count: 15 }],
    rewards: { wood: 20, food: 15, knowledge: 5 } },
  
  [q(4)]: { id: q(4), name: 'First Woodcutter', description: 'Build your first Woodcutter.', chapter: 1,
    objectives: [{ type: 'building', target: 'woodcutter', count: 1 }],
    rewards: { wood: 25, knowledge: 5 } },
  
  [q(5)]: { id: q(5), name: 'Basic Tools', description: 'Research Basic Tools.', chapter: 1,
    objectives: [{ type: 'tech', target: 'basic_tools' }],
    rewards: { knowledge: 10, wood: 20 } },
  
  [q(6)]: { id: q(6), name: 'Expanding Horizons', description: 'Explore 10 tiles.', chapter: 1,
    objectives: [{ type: 'tiles_explored', count: 10 }],
    rewards: { wood: 40, food: 30, gold: 20 } },
  
  [q(7)]: { id: q(7), name: 'Lumber Camp', description: 'Build 3 Woodcutters.', chapter: 1,
    objectives: [{ type: 'building', target: 'woodcutter', count: 3 }],
    rewards: { wood: 35, gold: 15 } },
  
  [q(8)]: { id: q(8), name: 'Foraging', description: 'Build a Forager.', chapter: 1,
    objectives: [{ type: 'building', target: 'forager', count: 1 }],
    rewards: { food: 30, wood: 15 } },
  
  [q(9)]: { id: q(9), name: 'Stockpile', description: 'Gather 75 wood.', chapter: 1,
    objectives: [{ type: 'resource', target: 'wood', count: 75 }],
    rewards: { wood: 30, knowledge: 10 } },
  
  [q(10)]: { id: q(10), name: 'Mining Tech', description: 'Research Mining.', chapter: 1,
    objectives: [{ type: 'tech', target: 'mining' }],
    rewards: { stone: 30, knowledge: 15 } },
  
  [q(11)]: { id: q(11), name: 'First Quarry', description: 'Build a Quarry.', chapter: 1,
    objectives: [{ type: 'building', target: 'quarry', count: 1 }],
    rewards: { stone: 40, wood: 20 } },
  
  [q(12)]: { id: q(12), name: 'First Expeditions', description: 'Complete 5 expeditions.', chapter: 1,
    objectives: [{ type: 'expeditions_total', count: 5 }],
    rewards: { food: 50, wood: 40, stone: 30 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 2: SETTLEMENT (13-24) - Rewards: 40-150
  // ═══════════════════════════════════════════════════════════════════════════
  [q(13)]: { id: q(13), name: 'Five Buildings', description: 'Own 5 buildings.', chapter: 2,
    objectives: [{ type: 'buildings_total', count: 5 }],
    rewards: { wood: 50, stone: 40, gold: 25 } },
  
  [q(14)]: { id: q(14), name: 'Agriculture', description: 'Research Agriculture.', chapter: 2,
    objectives: [{ type: 'tech', target: 'agriculture' }],
    rewards: { food: 60, knowledge: 20 } },
  
  [q(15)]: { id: q(15), name: 'First Farm', description: 'Build a Farm.', chapter: 2,
    objectives: [{ type: 'building', target: 'farm', count: 1 }],
    rewards: { food: 50, wood: 30 } },
  
  [q(16)]: { id: q(16), name: 'More Farms', description: 'Build 3 Farms.', chapter: 2,
    objectives: [{ type: 'building', target: 'farm', count: 3 }],
    rewards: { food: 75, population: 1 } },
  
  [q(17)]: { id: q(17), name: 'Construction', description: 'Research Construction.', chapter: 2,
    objectives: [{ type: 'tech', target: 'construction' }],
    rewards: { wood: 50, stone: 40 } },
  
  [q(18)]: { id: q(18), name: 'Charting the Realm', description: 'Explore 20 tiles.', chapter: 2,
    objectives: [{ type: 'tiles_explored', count: 20 }],
    rewards: { gold: 100, stone: 75, knowledge: 50 } },

  // ─── NEW: Dungeon Introduction & Level 5 Milestone (Chapter 2) ───
  'quest_018a': { id: 'quest_018a', name: 'First Dungeon', description: 'Clear the Ancient Ruins dungeon.', chapter: 2,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier1', count: 1 }],
    rewards: { gold: 150, starEssence: 25 } },

  'quest_018b': { id: 'quest_018b', name: 'Level 5 - Ruins I', description: 'Reach level 5 and clear Ancient Ruins twice.', chapter: 2,
    objectives: [{ type: 'character_level', count: 5 }, { type: 'dungeon_tier_cleared', tier: 'tier1', count: 2 }],
    rewards: { gold: 200, starEssence: 50 } },

  'quest_018c': { id: 'quest_018c', name: 'Dungeon Explorer', description: 'Clear 3 dungeons total.', chapter: 2,
    objectives: [{ type: 'dungeons_cleared', count: 3 }],
    rewards: { gold: 180, starEssence: 40 } },

  'quest_018d': { id: 'quest_018d', name: 'Monster Slayer', description: 'Defeat 3 dungeon bosses.', chapter: 2,
    objectives: [{ type: 'bosses_defeated', count: 3 }],
    rewards: { gold: 180, starEssence: 40 } },

  [q(19)]: { id: q(19), name: 'First House', description: 'Build a House.', chapter: 2,
    objectives: [{ type: 'building', target: 'house', count: 1 }],
    rewards: { population: 1, gold: 40 } },
  
  [q(20)]: { id: q(20), name: 'Housing', description: 'Build 3 Houses.', chapter: 2,
    objectives: [{ type: 'building', target: 'house', count: 3 }],
    rewards: { population: 2, gold: 60 } },
  
  [q(21)]: { id: q(21), name: 'Stone Wealth', description: 'Gather 100 stone.', chapter: 2,
    objectives: [{ type: 'resource', target: 'stone', count: 100 }],
    rewards: { stone: 75, gold: 40 } },
  
  [q(22)]: { id: q(22), name: 'Writing', description: 'Research Writing.', chapter: 2,
    objectives: [{ type: 'tech', target: 'writing' }],
    rewards: { knowledge: 40, culture: 20 } },
  
  [q(23)]: { id: q(23), name: 'First Library', description: 'Build a Library.', chapter: 2,
    objectives: [{ type: 'building', target: 'library', count: 1 }],
    rewards: { knowledge: 60, gold: 40 } },

  // ─── NEW: Equipment & Level 10 Milestone (Chapter 2) ───
  'quest_023a': { id: 'quest_023a', name: 'Collector\'s Start', description: 'Own 3 pieces of equipment.', chapter: 2,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'common', count: 3 }],
    rewards: { gold: 120 } },

  'quest_023b': { id: 'quest_023b', name: 'Quality Gear', description: 'Own an Uncommon or better piece of equipment.', chapter: 2,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'uncommon', count: 1 }],
    rewards: { gold: 180, starEssence: 30 } },

  'quest_023c': { id: 'quest_023c', name: 'Level 10 - Ruins II', description: 'Reach level 10 and clear Ancient Ruins 5 times.', chapter: 2,
    objectives: [{ type: 'character_level', count: 10 }, { type: 'dungeon_tier_cleared', tier: 'tier1', count: 5 }],
    rewards: { gold: 300, starEssence: 100 } },

  'quest_023d': { id: 'quest_023d', name: 'Ruins Master', description: 'Clear Ancient Ruins 8 times total.', chapter: 2,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier1', count: 8 }],
    rewards: { gold: 250, starEssence: 75 } },

  [q(24)]: { id: q(24), name: 'Seasoned Gatherer', description: 'Complete 20 expeditions.', chapter: 2,
    objectives: [{ type: 'expeditions_total', count: 20 }],
    rewards: { food: 150, wood: 120, gold: 80 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 3: IRON AGE (25-36) - Rewards: 100-400
  // ═══════════════════════════════════════════════════════════════════════════
  [q(25)]: { id: q(25), name: 'Ten Buildings', description: 'Own 10 buildings.', chapter: 3,
    objectives: [{ type: 'buildings_total', count: 10 }],
    rewards: { gold: 100, population: 1, knowledge: 50 } },
  
  [q(26)]: { id: q(26), name: 'Deep Mining', description: 'Research Deep Mining.', chapter: 3,
    objectives: [{ type: 'tech', target: 'deep_mining' }],
    rewards: { stone: 125, iron: 50 } },
  
  [q(27)]: { id: q(27), name: 'Metallurgy', description: 'Research Metallurgy.', chapter: 3,
    objectives: [{ type: 'tech', target: 'metallurgy' }],
    rewards: { iron: 100, knowledge: 50 } },
  
  [q(28)]: { id: q(28), name: 'Iron Mine', description: 'Build a Mine.', chapter: 3,
    objectives: [{ type: 'building', target: 'mine', count: 1 }],
    rewards: { iron: 100, tools: 15 } },
  
  [q(29)]: { id: q(29), name: 'Forge', description: 'Build a Forge.', chapter: 3,
    objectives: [{ type: 'building', target: 'forge', count: 1 }],
    rewards: { tools: 75, iron: 75 } },
  
  [q(30)]: { id: q(30), name: 'The Wide World', description: 'Explore 40 tiles.', chapter: 3,
    objectives: [{ type: 'tiles_explored', count: 40 }],
    rewards: { gold: 200, iron: 150, knowledge: 100 } },

  // ─── NEW: Star Force & Forging Introduction (Chapter 3) ───
  'quest_030a': { id: 'quest_030a', name: 'First Enhancement', description: 'Enhance an item to 1 star.', chapter: 3,
    objectives: [{ type: 'equipment_max_stars', count: 1 }],
    rewards: { starEssence: 75, gold: 200 } },

  'quest_030b': { id: 'quest_030b', name: 'Rising Star', description: 'Enhance an item to 3 stars.', chapter: 3,
    objectives: [{ type: 'equipment_max_stars', count: 3 }],
    rewards: { starEssence: 150, gold: 300 } },

  'quest_030c': { id: 'quest_030c', name: 'First Forging', description: 'Apply a scroll to any equipment.', chapter: 3,
    objectives: [{ type: 'equipment_scrolled', count: 1 }],
    rewards: { gold: 250 } },

  'quest_030d': { id: 'quest_030d', name: 'Scroll Collector', description: 'Apply 3 scrolls to equipment.', chapter: 3,
    objectives: [{ type: 'equipment_scrolled', count: 3 }],
    rewards: { gold: 350, starEssence: 100 } },

  [q(31)]: { id: q(31), name: 'Iron Working', description: 'Research Iron Working.', chapter: 3,
    objectives: [{ type: 'tech', target: 'iron_working' }],
    rewards: { iron: 125, tools: 60 } },
  
  [q(32)]: { id: q(32), name: 'More Forges', description: 'Build 2 Forges.', chapter: 3,
    objectives: [{ type: 'building', target: 'forge', count: 2 }],
    rewards: { tools: 100, gold: 100 } },
  
  [q(33)]: { id: q(33), name: 'Prospecting', description: 'Research Prospecting.', chapter: 3,
    objectives: [{ type: 'tech', target: 'prospecting' }],
    rewards: { gold: 175, knowledge: 50 } },
  
  [q(34)]: { id: q(34), name: 'Gold Mine', description: 'Build a Gold Mine.', chapter: 3,
    objectives: [{ type: 'building', target: 'goldMine', count: 1 }],
    rewards: { gold: 250, gems: 15 } },
  
  [q(35)]: { id: q(35), name: 'Literacy', description: 'Research Literacy.', chapter: 3,
    objectives: [{ type: 'tech', target: 'literacy' }],
    rewards: { knowledge: 100, culture: 50 } },

  // ─── NEW: Level 15 Milestone - Tier 2 Introduction (Chapter 3) ───
  'quest_035a': { id: 'quest_035a', name: 'Wonder of the World', description: 'Build your first Wonder.', chapter: 3,
    objectives: [{ type: 'wonders', count: 1 }],
    rewards: { gold: 400, culture: 200 } },

  'quest_035b': { id: 'quest_035b', name: 'Level 15 - Catacombs I', description: 'Reach level 15 and clear Dark Catacombs.', chapter: 3,
    objectives: [{ type: 'character_level', count: 15 }, { type: 'dungeon_tier_cleared', tier: 'tier2', count: 1 }],
    rewards: { gold: 500, starEssence: 150 } },

  'quest_035c': { id: 'quest_035c', name: 'Boss Hunter I', description: 'Defeat 5 dungeon bosses.', chapter: 3,
    objectives: [{ type: 'bosses_defeated', count: 5 }],
    rewards: { starEssence: 150, gold: 400 } },

  'quest_035d': { id: 'quest_035d', name: 'Dungeon Veteran', description: 'Clear 10 dungeons total.', chapter: 3,
    objectives: [{ type: 'dungeons_cleared', count: 10 }],
    rewards: { gold: 350, starEssence: 120 } },

  'quest_035e': { id: 'quest_035e', name: 'Rare Find', description: 'Own a Rare piece of equipment.', chapter: 3,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'rare', count: 1 }],
    rewards: { starEssence: 100, gold: 300 } },

  'quest_035f': { id: 'quest_035f', name: 'Five Star Equipment', description: 'Enhance an item to 5 stars.', chapter: 3,
    objectives: [{ type: 'equipment_max_stars', count: 5 }],
    rewards: { starEssence: 200, gold: 400 } },

  'quest_035g': { id: 'quest_035g', name: 'Equipped for Battle', description: 'Have equipment in 6 slots.', chapter: 3,
    objectives: [{ type: 'equipment_slots_filled', count: 6 }],
    rewards: { gold: 350, starEssence: 120 } },

  [q(36)]: { id: q(36), name: 'Frontier Outposts', description: 'Build on 50 tiles.', chapter: 3,
    objectives: [{ type: 'tiles_built', count: 50 }],
    rewards: { wood: 400, stone: 300, gold: 200 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 4: COMMERCE (37-48) - Rewards: 250-1K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(37)]: { id: q(37), name: 'Fifteen Buildings', description: 'Own 15 buildings.', chapter: 4,
    objectives: [{ type: 'buildings_total', count: 15 }],
    rewards: { gold: 300, iron: 150, knowledge: 100 } },
  
  [q(38)]: { id: q(38), name: 'Smelting', description: 'Research Smelting.', chapter: 4,
    objectives: [{ type: 'tech', target: 'smelting' }],
    rewards: { iron: 250, coal: 75 } },
  
  [q(39)]: { id: q(39), name: 'Smelter', description: 'Build a Smelter.', chapter: 4,
    objectives: [{ type: 'building', target: 'smelter', count: 1 }],
    rewards: { steel: 75, iron: 150 } },
  
  [q(40)]: { id: q(40), name: 'Currency', description: 'Research Currency.', chapter: 4,
    objectives: [{ type: 'tech', target: 'currency' }],
    rewards: { gold: 400, knowledge: 100 } },
  
  [q(41)]: { id: q(41), name: 'Market', description: 'Build a Market.', chapter: 4,
    objectives: [{ type: 'building', target: 'market', count: 1 }],
    rewards: { gold: 350, gems: 30 } },
  
  [q(42)]: { id: q(42), name: 'Woodlands Mastery', description: 'Complete 10 Forest expeditions.', chapter: 4,
    objectives: [{ type: 'expeditions_biome', target: 'forest', count: 10 }],
    rewards: { wood: 750, food: 400, gold: 300 } },

  // ─── NEW: Level 20 Milestone - Catacombs II (Chapter 4) ───
  'quest_042a': { id: 'quest_042a', name: 'Level 20 - Catacombs II', description: 'Reach level 20 and clear Dark Catacombs 3 times.', chapter: 4,
    objectives: [{ type: 'character_level', count: 20 }, { type: 'dungeon_tier_cleared', tier: 'tier2', count: 3 }],
    rewards: { gold: 700, starEssence: 200 } },

  'quest_042b': { id: 'quest_042b', name: 'Catacombs Conqueror', description: 'Clear Dark Catacombs 5 times.', chapter: 4,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier2', count: 5 }],
    rewards: { gold: 1000, starEssence: 250 } },

  'quest_042c': { id: 'quest_042c', name: 'Great Work', description: 'Build a Great Work on the world map.', chapter: 4,
    objectives: [{ type: 'great_works', count: 1 }],
    rewards: { gold: 800, culture: 400 } },

  'quest_042d': { id: 'quest_042d', name: 'Boss Hunter II', description: 'Defeat 10 dungeon bosses.', chapter: 4,
    objectives: [{ type: 'bosses_defeated', count: 10 }],
    rewards: { gold: 600, starEssence: 180 } },

  [q(43)]: { id: q(43), name: 'Trade Routes Tech', description: 'Research Trade Routes.', chapter: 4,
    objectives: [{ type: 'tech', target: 'trade_routes' }],
    rewards: { gold: 500, culture: 75 } },
  
  [q(44)]: { id: q(44), name: 'Trading Post', description: 'Build a Trading Post.', chapter: 4,
    objectives: [{ type: 'building', target: 'tradingPost', count: 1 }],
    rewards: { gold: 450, gems: 40 } },
  
  [q(45)]: { id: q(45), name: 'Banking', description: 'Research Banking.', chapter: 4,
    objectives: [{ type: 'tech', target: 'banking' }],
    rewards: { gold: 600, knowledge: 125 } },
  
  [q(46)]: { id: q(46), name: 'Bank', description: 'Build a Bank.', chapter: 4,
    objectives: [{ type: 'building', target: 'bank', count: 1 }],
    rewards: { gold: 750, gems: 50 } },
  
  [q(47)]: { id: q(47), name: 'Philosophy', description: 'Research Philosophy.', chapter: 4,
    objectives: [{ type: 'tech', target: 'philosophy' }],
    rewards: { knowledge: 200, culture: 125 } },

  // ─── NEW: Epic Equipment & Advanced Progress (Chapter 4) ───
  'quest_047a': { id: 'quest_047a', name: 'Epic Equipment', description: 'Own an Epic piece of equipment.', chapter: 4,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'epic', count: 1 }],
    rewards: { starEssence: 250, gold: 600 } },

  'quest_047b': { id: 'quest_047b', name: 'Level 20', description: 'Reach character level 20.', chapter: 4,
    objectives: [{ type: 'character_level', count: 20 }],
    rewards: { gold: 800, knowledge: 500 } },

  'quest_047c': { id: 'quest_047c', name: 'Boss Slayer III', description: 'Defeat 15 dungeon bosses.', chapter: 4,
    objectives: [{ type: 'bosses_defeated', count: 15 }],
    rewards: { starEssence: 400, gold: 1000 } },

  'quest_047d': { id: 'quest_047d', name: 'Wonder Duo', description: 'Build 2 Wonders.', chapter: 4,
    objectives: [{ type: 'wonders', count: 2 }],
    rewards: { gold: 1200, culture: 600 } },

  'quest_047e': { id: 'quest_047e', name: 'Seven Stars', description: 'Enhance an item to 7 stars.', chapter: 4,
    objectives: [{ type: 'equipment_max_stars', count: 7 }],
    rewards: { starEssence: 350, gold: 900 } },

  'quest_047f': { id: 'quest_047f', name: 'Dungeon Runner', description: 'Clear 20 dungeons.', chapter: 4,
    objectives: [{ type: 'dungeons_cleared', count: 20 }],
    rewards: { gold: 1100, starEssence: 300 } },

  'quest_047g': { id: 'quest_047g', name: 'Scroll Enthusiast', description: 'Apply 10 scrolls to equipment.', chapter: 4,
    objectives: [{ type: 'equipment_scrolled', count: 10 }],
    rewards: { gold: 800, starEssence: 200 } },

  [q(48)]: { id: q(48), name: 'Realm Cartographer', description: 'Explore 80 tiles.', chapter: 4,
    objectives: [{ type: 'tiles_explored', count: 80 }],
    rewards: { gold: 1000, knowledge: 400, gems: 75 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 5: INDUSTRY (49-60) - Rewards: 600-2.5K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(49)]: { id: q(49), name: 'Twenty Buildings', description: 'Own 20 buildings.', chapter: 5,
    objectives: [{ type: 'buildings_total', count: 20 }],
    rewards: { gold: 600, iron: 300, knowledge: 200 } },
  
  [q(50)]: { id: q(50), name: 'Engineering', description: 'Research Engineering.', chapter: 5,
    objectives: [{ type: 'tech', target: 'engineering' }],
    rewards: { tools: 250, knowledge: 200 } },
  
  [q(51)]: { id: q(51), name: 'Workshop', description: 'Build a Workshop.', chapter: 5,
    objectives: [{ type: 'building', target: 'workshop', count: 1 }],
    rewards: { tools: 300, machinery: 75 } },
  
  [q(52)]: { id: q(52), name: 'Education', description: 'Research Education.', chapter: 5,
    objectives: [{ type: 'tech', target: 'education' }],
    rewards: { knowledge: 400, gold: 300 } },
  
  [q(53)]: { id: q(53), name: 'Academy', description: 'Build an Academy.', chapter: 5,
    objectives: [{ type: 'building', target: 'academy', count: 1 }],
    rewards: { knowledge: 500, culture: 200 } },
  
  [q(54)]: { id: q(54), name: 'Harvest of the Plains', description: 'Complete 20 Plains expeditions.', chapter: 5,
    objectives: [{ type: 'expeditions_biome', target: 'plains', count: 20 }],
    rewards: { food: 2000, gold: 750, culture: 250 } },

  // ─── NEW: Tier 3 Dungeons & Advanced Progress (Chapter 5) ───
  'quest_054a': { id: 'quest_054a', name: 'Temple Raider', description: 'Clear a Corrupted Temple (Tier 3) dungeon.', chapter: 5,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier3', count: 1 }],
    rewards: { gold: 1500, starEssence: 300 } },

  'quest_054b': { id: 'quest_054b', name: 'Temple Master', description: 'Clear Corrupted Temple 5 times.', chapter: 5,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier3', count: 5 }],
    rewards: { gold: 2500, starEssence: 500 } },

  'quest_054c': { id: 'quest_054c', name: 'Scroll Master', description: 'Apply 20 scrolls to equipment.', chapter: 5,
    objectives: [{ type: 'equipment_scrolled', count: 20 }],
    rewards: { gold: 2000, starEssence: 400 } },

  'quest_054d': { id: 'quest_054d', name: 'Nine Stars', description: 'Enhance an item to 9 stars.', chapter: 5,
    objectives: [{ type: 'equipment_max_stars', count: 9 }],
    rewards: { starEssence: 600, gold: 1800 } },

  [q(55)]: { id: q(55), name: 'Machinery', description: 'Research Machinery.', chapter: 5,
    objectives: [{ type: 'tech', target: 'machinery' }],
    rewards: { machinery: 200, tools: 250 } },
  
  [q(56)]: { id: q(56), name: 'Factory', description: 'Build a Factory.', chapter: 5,
    objectives: [{ type: 'building', target: 'factory', count: 1 }],
    rewards: { machinery: 250, steel: 200 } },
  
  [q(57)]: { id: q(57), name: 'Architecture', description: 'Research Architecture.', chapter: 5,
    objectives: [{ type: 'tech', target: 'architecture' }],
    rewards: { stone: 500, knowledge: 300 } },
  
  [q(58)]: { id: q(58), name: 'Cathedral', description: 'Build a Cathedral.', chapter: 5,
    objectives: [{ type: 'building', target: 'cathedral', count: 1 }],
    rewards: { culture: 400, gold: 500 } },
  
  [q(59)]: { id: q(59), name: 'Steel Production', description: 'Research Steel Production.', chapter: 5,
    objectives: [{ type: 'tech', target: 'steel_production' }],
    rewards: { steel: 300, iron: 400 } },

  // ─── NEW: Character & Equipment Progress (Chapter 5) ───
  'quest_059a': { id: 'quest_059a', name: 'Level 25 Milestone', description: 'Reach character level 25.', chapter: 5,
    objectives: [{ type: 'character_level', count: 25 }],
    rewards: { gold: 1500, energy: 800 } },

  'quest_059b': { id: 'quest_059b', name: 'Legendary Find', description: 'Own a Legendary piece of equipment.', chapter: 5,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'legendary', count: 1 }],
    rewards: { starEssence: 750, gold: 2000 } },

  'quest_059c': { id: 'quest_059c', name: 'Three Wonders', description: 'Build 3 Wonders.', chapter: 5,
    objectives: [{ type: 'wonders', count: 3 }],
    rewards: { gold: 2500, culture: 1500 } },

  'quest_059d': { id: 'quest_059d', name: 'Two Great Works', description: 'Build 2 Great Works.', chapter: 5,
    objectives: [{ type: 'great_works', count: 2 }],
    rewards: { gold: 2000, steel: 1000 } },

  [q(60)]: { id: q(60), name: 'Territorial Claims', description: 'Build on 100 tiles.', chapter: 5,
    objectives: [{ type: 'tiles_built', count: 100 }],
    rewards: { gold: 1500, steel: 500, tools: 400 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 6: POWER (61-72) - Rewards: 1.5K-6K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(61)]: { id: q(61), name: 'Thirty Buildings', description: 'Own 30 buildings.', chapter: 6,
    objectives: [{ type: 'buildings_total', count: 30 }],
    rewards: { gold: 1500, machinery: 400, knowledge: 500 } },
  
  [q(62)]: { id: q(62), name: 'Coal Mining', description: 'Research Coal Mining.', chapter: 6,
    objectives: [{ type: 'tech', target: 'coal_mining' }],
    rewards: { coal: 500, knowledge: 400 } },
  
  [q(63)]: { id: q(63), name: 'Coal Mine', description: 'Build a Coal Mine.', chapter: 6,
    objectives: [{ type: 'building', target: 'coalMine', count: 1 }],
    rewards: { coal: 750, gold: 600 } },
  
  [q(64)]: { id: q(64), name: 'Power Generation', description: 'Research Power Generation.', chapter: 6,
    objectives: [{ type: 'tech', target: 'power_generation' }],
    rewards: { energy: 400, machinery: 500 } },
  
  [q(65)]: { id: q(65), name: 'Power Plant', description: 'Build a Power Plant.', chapter: 6,
    objectives: [{ type: 'building', target: 'powerPlant', count: 1 }],
    rewards: { energy: 600, steel: 500 } },
  
  [q(66)]: { id: q(66), name: 'Mountain Conquest', description: 'Complete 30 Mountains expeditions.', chapter: 6,
    objectives: [{ type: 'expeditions_biome', target: 'mountains', count: 30 }],
    rewards: { stone: 3000, iron: 2000, gems: 150 } },

  // ─── NEW: Advanced Dungeon Progress (Chapter 6) ───
  'quest_066a': { id: 'quest_066a', name: 'Dungeon Master', description: 'Clear 50 dungeons total.', chapter: 6,
    objectives: [{ type: 'dungeons_cleared', count: 50 }],
    rewards: { starEssence: 1000, gold: 4000 } },

  'quest_066b': { id: 'quest_066b', name: 'Ten Star Equipment', description: 'Enhance an item to 10 stars.', chapter: 6,
    objectives: [{ type: 'equipment_max_stars', count: 10 }],
    rewards: { starEssence: 1500, gold: 3000 } },

  'quest_066c': { id: 'quest_066c', name: 'Boss Vanquisher', description: 'Defeat 30 dungeon bosses.', chapter: 6,
    objectives: [{ type: 'bosses_defeated', count: 30 }],
    rewards: { starEssence: 800, gold: 3500 } },

  [q(67)]: { id: q(67), name: 'Industrialization', description: 'Research Industrialization.', chapter: 6,
    objectives: [{ type: 'tech', target: 'industrialization' }],
    rewards: { energy: 750, knowledge: 600 } },
  
  [q(68)]: { id: q(68), name: 'Steel Mill', description: 'Build a Steel Mill.', chapter: 6,
    objectives: [{ type: 'building', target: 'steelMill', count: 1 }],
    rewards: { steel: 1000, energy: 500 } },
  
  [q(69)]: { id: q(69), name: 'Scientific Method', description: 'Research Scientific Method.', chapter: 6,
    objectives: [{ type: 'tech', target: 'scientific_method' }],
    rewards: { science: 500, knowledge: 750 } },
  
  [q(70)]: { id: q(70), name: 'Laboratory', description: 'Build a Laboratory.', chapter: 6,
    objectives: [{ type: 'building', target: 'laboratory', count: 1 }],
    rewards: { science: 750, knowledge: 600 } },
  
  [q(71)]: { id: q(71), name: 'Higher Education', description: 'Research Higher Education.', chapter: 6,
    objectives: [{ type: 'tech', target: 'higher_education' }],
    rewards: { knowledge: 1000, culture: 500 } },

  // ─── NEW: Character & Great Works (Chapter 6) ───
  'quest_071a': { id: 'quest_071a', name: 'Level 35', description: 'Reach character level 35.', chapter: 6,
    objectives: [{ type: 'character_level', count: 35 }],
    rewards: { gold: 4000, science: 2000 } },

  'quest_071b': { id: 'quest_071b', name: 'Three Great Works', description: 'Build 3 Great Works.', chapter: 6,
    objectives: [{ type: 'great_works', count: 3 }],
    rewards: { gold: 5000, energy: 3000 } },

  'quest_071c': { id: 'quest_071c', name: 'Full Equipment', description: 'Have equipment in all 12 slots.', chapter: 6,
    objectives: [{ type: 'equipment_slots_filled', count: 12 }],
    rewards: { starEssence: 1000, gold: 4000 } },

  [q(72)]: { id: q(72), name: 'Seasoned Expeditioner', description: 'Complete 40 expeditions.', chapter: 6,
    objectives: [{ type: 'expeditions_total', count: 40 }],
    rewards: { gold: 3000, energy: 1500, steel: 1000 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 7: EMPIRE (73-84) - Rewards: 4K-15K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(73)]: { id: q(73), name: 'Forty Buildings', description: 'Own 40 buildings.', chapter: 7,
    objectives: [{ type: 'buildings_total', count: 40 }],
    rewards: { gold: 4000, energy: 1500, science: 1000 } },
  
  [q(74)]: { id: q(74), name: 'Grand Architecture', description: 'Research Grand Architecture.', chapter: 7,
    objectives: [{ type: 'tech', target: 'grand_architecture' }],
    rewards: { culture: 2000, gold: 3000 } },
  
  [q(75)]: { id: q(75), name: 'Palace', description: 'Build a Palace.', chapter: 7,
    objectives: [{ type: 'building', target: 'palace', count: 1 }],
    rewards: { culture: 3000, gold: 4000 } },
  
  [q(76)]: { id: q(76), name: 'Tactics', description: 'Research Military Tactics.', chapter: 7,
    objectives: [{ type: 'tech', target: 'tactics' }],
    rewards: { steel: 2000, knowledge: 1500 } },
  
  [q(77)]: { id: q(77), name: 'Fortress', description: 'Build a Fortress.', chapter: 7,
    objectives: [{ type: 'building', target: 'fortress', count: 1 }],
    rewards: { steel: 3000, gold: 3500 } },
  
  [q(78)]: { id: q(78), name: 'Coastal Dominion', description: 'Complete 40 Coastal expeditions.', chapter: 7,
    objectives: [{ type: 'expeditions_biome', target: 'coastal', count: 40 }],
    rewards: { gold: 10000, food: 5000, knowledge: 3000 } },

  // ─── NEW: Tier 4 Dungeons (Chapter 7) ───
  'quest_078a': { id: 'quest_078a', name: 'Abyssal Explorer', description: 'Clear an Abyssal Depths (Tier 4) dungeon.', chapter: 7,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier4', count: 1 }],
    rewards: { gold: 8000, starEssence: 1500 } },

  'quest_078b': { id: 'quest_078b', name: 'Abyssal Conqueror', description: 'Clear Abyssal Depths 5 times.', chapter: 7,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier4', count: 5 }],
    rewards: { gold: 12000, starEssence: 3000 } },

  'quest_078c': { id: 'quest_078c', name: 'Twelve Stars', description: 'Enhance an item to 12 stars.', chapter: 7,
    objectives: [{ type: 'equipment_max_stars', count: 12 }],
    rewards: { starEssence: 3000, gold: 8000 } },

  [q(79)]: { id: q(79), name: 'Astronomy', description: 'Research Astronomy.', chapter: 7,
    objectives: [{ type: 'tech', target: 'astronomy' }],
    rewards: { science: 2000, knowledge: 2500 } },
  
  [q(80)]: { id: q(80), name: 'Observatory', description: 'Build an Observatory.', chapter: 7,
    objectives: [{ type: 'building', target: 'observatory', count: 1 }],
    rewards: { science: 2500, crystals: 75 } },
  
  [q(81)]: { id: q(81), name: 'Chemistry', description: 'Research Chemistry.', chapter: 7,
    objectives: [{ type: 'tech', target: 'chemistry' }],
    rewards: { science: 2000, steel: 2000 } },
  
  [q(82)]: { id: q(82), name: 'Grand Castle', description: 'Build a Grand Castle.', chapter: 7,
    objectives: [{ type: 'building', target: 'grandCastle', count: 1 }],
    rewards: { culture: 4000, gold: 5000 } },
  
  [q(83)]: { id: q(83), name: 'Monarchy', description: 'Research Monarchy.', chapter: 7,
    objectives: [{ type: 'tech', target: 'monarchy' }],
    rewards: { knowledge: 3000, culture: 2000 } },

  // ─── NEW: Character & Equipment Mastery (Chapter 7) ───
  'quest_083a': { id: 'quest_083a', name: 'Level 45', description: 'Reach character level 45.', chapter: 7,
    objectives: [{ type: 'character_level', count: 45 }],
    rewards: { gold: 10000, culture: 5000 } },

  'quest_083b': { id: 'quest_083b', name: 'Epic Collection', description: 'Own 6 Epic or better pieces of equipment.', chapter: 7,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'epic', count: 6 }],
    rewards: { starEssence: 2000, gold: 8000 } },

  'quest_083c': { id: 'quest_083c', name: 'Four Great Works', description: 'Build 4 Great Works.', chapter: 7,
    objectives: [{ type: 'great_works', count: 4 }],
    rewards: { gold: 12000, steel: 6000 } },

  [q(84)]: { id: q(84), name: 'Imperial Reach', description: 'Build on 150 tiles.', chapter: 7,
    objectives: [{ type: 'tiles_built', count: 150 }],
    rewards: { gold: 10000, steel: 4000, machinery: 2500 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 8: SCIENCE (85-96) - Rewards: 10K-40K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(85)]: { id: q(85), name: 'Fifty Buildings', description: 'Own 50 buildings.', chapter: 8,
    objectives: [{ type: 'buildings_total', count: 50 }],
    rewards: { gold: 10000, science: 4000, culture: 3000 } },
  
  [q(86)]: { id: q(86), name: 'Physics', description: 'Research Physics.', chapter: 8,
    objectives: [{ type: 'tech', target: 'physics' }],
    rewards: { science: 5000, knowledge: 5000 } },
  
  [q(87)]: { id: q(87), name: 'University', description: 'Build a University.', chapter: 8,
    objectives: [{ type: 'building', target: 'university', count: 1 }],
    rewards: { science: 6000, energy: 3000 } },
  
  [q(88)]: { id: q(88), name: 'Arcane Arts', description: 'Research Arcane Arts.', chapter: 8,
    objectives: [{ type: 'tech', target: 'arcane_arts' }],
    rewards: { magic: 3000, knowledge: 6000 } },
  
  [q(89)]: { id: q(89), name: 'Wizard Tower', description: 'Build a Wizard Tower.', chapter: 8,
    objectives: [{ type: 'building', target: 'wizardTower', count: 1 }],
    rewards: { magic: 4000, crystals: 400 } },
  
  [q(90)]: { id: q(90), name: 'Secrets of the Ancients', description: 'Complete 50 Ruins expeditions.', chapter: 8,
    objectives: [{ type: 'expeditions_biome', target: 'ruins', count: 50 }],
    rewards: { knowledge: 20000, gold: 12000, artifacts: 100 } },

  // ─── NEW: Dungeon Legend & Mythic (Chapter 8) ───
  'quest_090a': { id: 'quest_090a', name: 'Dungeon Legend', description: 'Clear 100 dungeons total.', chapter: 8,
    objectives: [{ type: 'dungeons_cleared', count: 100 }],
    rewards: { starEssence: 5000, gold: 20000 } },

  'quest_090b': { id: 'quest_090b', name: 'Mythic Discovery', description: 'Own a Mythic piece of equipment.', chapter: 8,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'mythic', count: 1 }],
    rewards: { starEssence: 5000, gold: 25000 } },

  'quest_090c': { id: 'quest_090c', name: 'Perfected Enhancement', description: 'Enhance an item to 15 stars (maximum).', chapter: 8,
    objectives: [{ type: 'equipment_max_stars', count: 15 }],
    rewards: { starEssence: 10000, gold: 30000, artifacts: 500 } },

  [q(91)]: { id: q(91), name: 'Advanced Research', description: 'Research Advanced Research.', chapter: 8,
    objectives: [{ type: 'tech', target: 'advanced_research' }],
    rewards: { science: 8000, knowledge: 10000 } },
  
  [q(92)]: { id: q(92), name: 'Research Center', description: 'Build a Research Center.', chapter: 8,
    objectives: [{ type: 'building', target: 'researchCenter', count: 1 }],
    rewards: { science: 10000, artifacts: 50 } },
  
  [q(93)]: { id: q(93), name: 'Alchemy', description: 'Research Alchemy.', chapter: 8,
    objectives: [{ type: 'tech', target: 'alchemy' }],
    rewards: { magic: 5000, science: 5000 } },
  
  [q(94)]: { id: q(94), name: 'Alchemy Lab', description: 'Build an Alchemy Lab.', chapter: 8,
    objectives: [{ type: 'building', target: 'alchemyLab', count: 1 }],
    rewards: { magic: 6000, crystals: 1000 } },
  
  [q(95)]: { id: q(95), name: 'Enchanting', description: 'Research Enchanting.', chapter: 8,
    objectives: [{ type: 'tech', target: 'enchanting' }],
    rewards: { magic: 7000, gems: 500 } },

  // ─── NEW: Character & Boss Progress (Chapter 8) ───
  'quest_095a': { id: 'quest_095a', name: 'Level 55', description: 'Reach character level 55.', chapter: 8,
    objectives: [{ type: 'character_level', count: 55 }],
    rewards: { gold: 25000, magic: 10000 } },

  'quest_095b': { id: 'quest_095b', name: 'Boss Destroyer', description: 'Defeat 75 dungeon bosses.', chapter: 8,
    objectives: [{ type: 'bosses_defeated', count: 75 }],
    rewards: { starEssence: 4000, gold: 20000 } },

  'quest_095c': { id: 'quest_095c', name: 'Legendary Arsenal', description: 'Own 6 Legendary pieces of equipment.', chapter: 8,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'legendary', count: 6 }],
    rewards: { starEssence: 8000, gold: 35000 } },

  [q(96)]: { id: q(96), name: 'Known World', description: 'Explore 160 tiles.', chapter: 8,
    objectives: [{ type: 'tiles_explored', count: 160 }],
    rewards: { gold: 25000, science: 10000, knowledge: 10000 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 9: MAGIC (97-108) - Rewards: 25K-100K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(97)]: { id: q(97), name: 'Sixty Buildings', description: 'Own 60 buildings.', chapter: 9,
    objectives: [{ type: 'buildings_total', count: 60 }],
    rewards: { gold: 25000, science: 10000, magic: 7500 } },
  
  [q(98)]: { id: q(98), name: 'Crystal Synthesis', description: 'Research Crystal Synthesis.', chapter: 9,
    objectives: [{ type: 'tech', target: 'crystal_synthesis' }],
    rewards: { crystals: 5000, magic: 10000 } },
  
  [q(99)]: { id: q(99), name: 'Enchanter', description: 'Build an Enchanter.', chapter: 9,
    objectives: [{ type: 'building', target: 'enchanter', count: 1 }],
    rewards: { magic: 12000, artifacts: 200 } },
  
  [q(100)]: { id: q(100), name: 'Artifact Crafting', description: 'Research Artifact Crafting.', chapter: 9,
    objectives: [{ type: 'tech', target: 'artifact_crafting' }],
    rewards: { artifacts: 1000, magic: 8000 } },
  
  [q(101)]: { id: q(101), name: 'Gem Mine', description: 'Build a Gem Mine.', chapter: 9,
    objectives: [{ type: 'building', target: 'gemMine', count: 1 }],
    rewards: { gems: 2500, crystals: 2000 } },
  
  [q(102)]: { id: q(102), name: 'Volcanic Ventures', description: 'Complete 30 Volcanic expeditions.', chapter: 9,
    objectives: [{ type: 'expeditions_biome', target: 'volcanic', count: 30 }],
    rewards: { iron: 25000, coal: 20000, energy: 15000 } },

  // ─── NEW: Tier 5 Dungeons (Chapter 9) ───
  'quest_102a': { id: 'quest_102a', name: 'Void Walker', description: 'Clear a Void Citadel (Tier 5) dungeon.', chapter: 9,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier5', count: 1 }],
    rewards: { gold: 40000, starEssence: 8000 } },

  'quest_102b': { id: 'quest_102b', name: 'Void Master', description: 'Clear Void Citadel 5 times.', chapter: 9,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier5', count: 5 }],
    rewards: { gold: 60000, starEssence: 15000 } },

  'quest_102c': { id: 'quest_102c', name: 'Level 65', description: 'Reach character level 65.', chapter: 9,
    objectives: [{ type: 'character_level', count: 65 }],
    rewards: { gold: 50000, magic: 25000 } },

  [q(103)]: { id: q(103), name: 'Mysticism', description: 'Research Mysticism.', chapter: 9,
    objectives: [{ type: 'tech', target: 'mysticism' }],
    rewards: { magic: 15000, science: 10000 } },
  
  [q(104)]: { id: q(104), name: 'Sanctum', description: 'Build a Sanctum.', chapter: 9,
    objectives: [{ type: 'building', target: 'sanctum', count: 1 }],
    rewards: { magic: 20000, artifacts: 500 } },
  
  [q(105)]: { id: q(105), name: 'Planar Theory', description: 'Research Planar Theory.', chapter: 9,
    objectives: [{ type: 'tech', target: 'planar_theory' }],
    rewards: { magic: 18000, science: 12000 } },
  
  [q(106)]: { id: q(106), name: 'Artifact Forge', description: 'Build an Artifact Forge.', chapter: 9,
    objectives: [{ type: 'building', target: 'artifactForge', count: 1 }],
    rewards: { artifacts: 2000, crystals: 3000 } },
  
  [q(107)]: { id: q(107), name: 'Eternal Materials', description: 'Research Eternal Materials.', chapter: 9,
    objectives: [{ type: 'tech', target: 'eternal_materials' }],
    rewards: { steel: 30000, magic: 15000 } },

  // ─── NEW: Mythic Arsenal & Dungeon Mastery (Chapter 9) ───
  'quest_107a': { id: 'quest_107a', name: 'Mythic Arsenal', description: 'Own 3 Mythic pieces of equipment.', chapter: 9,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'mythic', count: 3 }],
    rewards: { starEssence: 20000, gold: 75000 } },

  'quest_107b': { id: 'quest_107b', name: 'Ultimate Boss Hunter', description: 'Defeat 100 dungeon bosses.', chapter: 9,
    objectives: [{ type: 'bosses_defeated', count: 100 }],
    rewards: { starEssence: 15000, gold: 60000, artifacts: 2000 } },

  'quest_107c': { id: 'quest_107c', name: 'Dungeon Champion', description: 'Clear 200 dungeons total.', chapter: 9,
    objectives: [{ type: 'dungeons_cleared', count: 200 }],
    rewards: { starEssence: 25000, gold: 100000 } },

  [q(108)]: { id: q(108), name: 'Veteran Expeditioner', description: 'Complete 80 expeditions.', chapter: 9,
    objectives: [{ type: 'expeditions_total', count: 80 }],
    rewards: { gold: 50000, magic: 15000, artifacts: 3000 } },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHAPTER 10: ASCENSION (109-120) - Rewards: 60K-250K
  // ═══════════════════════════════════════════════════════════════════════════
  [q(109)]: { id: q(109), name: 'Seventy Buildings', description: 'Own 70 buildings.', chapter: 10,
    objectives: [{ type: 'buildings_total', count: 70 }],
    rewards: { gold: 60000, magic: 25000, artifacts: 5000 } },
  
  [q(110)]: { id: q(110), name: 'Cosmic Awareness', description: 'Research Cosmic Awareness.', chapter: 10,
    objectives: [{ type: 'tech', target: 'cosmic_awareness' }],
    rewards: { magic: 40000, crystals: 15000 } },
  
  [q(111)]: { id: q(111), name: 'Ley Lines', description: 'Research Ley Lines.', chapter: 10,
    objectives: [{ type: 'tech', target: 'ley_lines' }],
    rewards: { magic: 50000, energy: 25000 } },
  
  [q(112)]: { id: q(112), name: 'Arcane Nexus', description: 'Build an Arcane Nexus.', chapter: 10,
    objectives: [{ type: 'building', target: 'arcaneNexus', count: 1 }],
    rewards: { magic: 75000, crystals: 10000 } },
  
  [q(113)]: { id: q(113), name: 'Universal Knowledge', description: 'Research Universal Knowledge.', chapter: 10,
    objectives: [{ type: 'tech', target: 'universal_knowledge' }],
    rewards: { science: 50000, knowledge: 60000 } },
  
  [q(114)]: { id: q(114), name: 'Mystic Communion', description: 'Complete 30 Mystic Grove expeditions.', chapter: 10,
    objectives: [{ type: 'expeditions_biome', target: 'mysticGrove', count: 30 }],
    rewards: { magic: 80000, crystals: 25000, artifacts: 8000 } },

  // ─── NEW: Level & Star Force Mastery (Chapter 10) ───
  'quest_114a': { id: 'quest_114a', name: 'Level 80', description: 'Reach character level 80.', chapter: 10,
    objectives: [{ type: 'character_level', count: 80 }],
    rewards: { gold: 100000, magic: 50000 } },

  'quest_114b': { id: 'quest_114b', name: 'Star Force Mastery', description: 'Have 3 items at 15 stars.', chapter: 10,
    objectives: [{ type: 'equipment_stars_15_count', count: 3 }],
    rewards: { starEssence: 50000, artifacts: 5000 } },

  'quest_114c': { id: 'quest_114c', name: 'Void Dominator', description: 'Clear Void Citadel 20 times.', chapter: 10,
    objectives: [{ type: 'dungeon_tier_cleared', tier: 'tier5', count: 20 }],
    rewards: { gold: 150000, magic: 75000 } },

  [q(115)]: { id: q(115), name: 'Eternal Library', description: 'Build an Eternal Library.', chapter: 10,
    objectives: [{ type: 'building', target: 'eternalLibrary', count: 1 }],
    rewards: { knowledge: 100000, artifacts: 10000 } },
  
  [q(116)]: { id: q(116), name: 'Dimensional Theory', description: 'Research Dimensional Theory.', chapter: 10,
    objectives: [{ type: 'tech', target: 'dimensional_theory' }],
    rewards: { magic: 75000, science: 60000 } },
  
  [q(117)]: { id: q(117), name: 'Starlight Harvesting', description: 'Research Starlight Harvesting.', chapter: 10,
    objectives: [{ type: 'tech', target: 'starlight_harvesting' }],
    rewards: { crystals: 20000, magic: 60000 } },
  
  [q(118)]: { id: q(118), name: 'Arcane Nexus II', description: 'Build a second Arcane Nexus.', chapter: 10,
    objectives: [{ type: 'building', target: 'arcaneNexus', count: 2 }],
    rewards: { magic: 50000, crystals: 25000 } },

  // ─── NEW: Ultimate Achievements (Chapter 10) ───
  'quest_118a': { id: 'quest_118a', name: 'Maximum Level', description: 'Reach character level 100.', chapter: 10,
    objectives: [{ type: 'character_level', count: 100 }],
    rewards: { gold: 200000, magic: 100000, artifacts: 10000 } },

  'quest_118b': { id: 'quest_118b', name: 'Full Mythic', description: 'Own 12 Mythic pieces of equipment (all slots).', chapter: 10,
    objectives: [{ type: 'equipment_rarity_owned', rarity: 'mythic', count: 12 }],
    rewards: { starEssence: 100000, gold: 250000 } },

  'quest_118c': { id: 'quest_118c', name: 'Perfect Equipment', description: 'Have all 12 equipment slots at 15 stars.', chapter: 10,
    objectives: [{ type: 'equipment_stars_15_count', count: 12 }],
    rewards: { starEssence: 200000, artifacts: 25000 } },

  'quest_118d': { id: 'quest_118d', name: 'Ultimate Champion', description: 'Defeat 250 dungeon bosses.', chapter: 10,
    objectives: [{ type: 'bosses_defeated', count: 250 }],
    rewards: { gold: 300000, magic: 150000 } },

  'quest_118e': { id: 'quest_118e', name: 'Five Great Works', description: 'Build the maximum 5 Great Works.', chapter: 10,
    objectives: [{ type: 'great_works', count: 5 }],
    rewards: { gold: 200000, culture: 100000 } },

  [q(119)]: { id: q(119), name: 'Master of the Realm', description: 'Build on 250 tiles. Complete 120 expeditions.', chapter: 10,
    objectives: [
      { type: 'tiles_built', count: 250 },
      { type: 'expeditions_total', count: 120 }
    ],
    rewards: { gold: 200000, magic: 75000, artifacts: 15000 } },
  
  // 🏆 VICTORY QUEST
  [q(120)]: { id: q(120), name: 'The Way Home', description: 'Build the Dimensional Beacon and return home.', chapter: 10,
    objectives: [{ type: 'building', target: 'dimensionalBeacon', count: 1 }],
    rewards: { gold: 500000, magic: 100000, artifacts: 25000 },
    isVictory: true },
};

// ═══════════════════════════════════════════════════════════════════════════
// CHAPTER DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════
export const chapters = {
  1: { name: 'Awakening', quests: 12, description: 'Your journey begins' },
  2: { name: 'Settlement', quests: 12, description: 'Build a home' },
  3: { name: 'Iron Age', quests: 12, description: 'Master metallurgy' },
  4: { name: 'Commerce', quests: 12, description: 'Trade and prosper' },
  5: { name: 'Industry', quests: 12, description: 'The age of machines' },
  6: { name: 'Power', quests: 12, description: 'Harness energy' },
  7: { name: 'Empire', quests: 12, description: 'Rule the realm' },
  8: { name: 'Science', quests: 12, description: 'Discover truths' },
  9: { name: 'Magic', quests: 12, description: 'Unlock arcane power' },
  10: { name: 'Ascension', quests: 12, description: 'Find your way home' },
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS (moved to index.js for modularity)
// ═══════════════════════════════════════════════════════════════════════════

export default mainQuests;
